/**
 * BasicInfoDrawer — Edit device info form (description, serial, behavior/profile, assignment).
 *
 * Uses the dp-drawer always-render pattern for smooth slide-in animation.
 * Conditionally renders:
 *   - Inovonics: Behavior dropdown (from useMapData)
 *   - Palcare:   Line Profile dropdown (from dspProfiles)
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import SearchableDropdown from '../../../../components/component-assets/SearchableDropdown.jsx';

const BasicInfoDrawer = ({
  open, onClose, onSave, saving,
  description: initDesc, serialNum: initSerial, deviceUseId: initUseId,
  isAreaDevice, areaId: initAreaId, residentId: initResidentId,
  areas, residents, useMapData,
  /* Palcare-specific props */
  isPalcare, dspProfileId: initDspProfileId, dspProfiles,
}) => {
  const [description, setDescription] = useState('');
  const [serialNum, setSerialNum] = useState('');
  const [deviceUseId, setDeviceUseId] = useState(0);
  const [dspProfileId, setDspProfileId] = useState(0);
  const [linkedId, setLinkedId] = useState(0);
  const [errors, setErrors] = useState({});

  // Populate form from props only when drawer opens (false → true)
  const prevOpen = useRef(false);
  useEffect(() => {
    if (open && !prevOpen.current) {
      setDescription(initDesc || '');
      setSerialNum(initSerial || '');
      setDeviceUseId(initUseId || 0);
      setDspProfileId(initDspProfileId || 0);
      setLinkedId(isAreaDevice ? (initAreaId || 0) : (initResidentId || 0));
      setErrors({});
    }
    prevOpen.current = open;
  });

  const validate = useCallback(() => {
    const errs = {};
    if (!serialNum || !/^[0-9]+$/.test(serialNum)) {
      errs.serialNum = 'Serial number must be numeric.';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }, [serialNum]);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    if (!validate()) return;
    onSave({
      description: description.trim(),
      serialNum,
      deviceUseId,
      dspProfileId,
      linkedId,
    });
  }, [validate, onSave, description, serialNum, deviceUseId, dspProfileId, linkedId]);

  return (
    <div className={`dp-drawer${open ? ' is-open' : ''}`} style={{ zIndex: 1001 }}>
      <div className="dp-drawer__header">
        <h2 className="dp-drawer__title">Edit Device Info</h2>
        <button className="dp-drawer__close" onClick={onClose}>✕</button>
      </div>

      <form className="dp-drawer__body" onSubmit={handleSubmit}>
        {/* Description */}
        <div className="dp-drawer__field">
          <label className="dp-drawer__label">
            Description
          </label>
          <input
            type="text"
            className={`dp-drawer__input${errors.description ? ' is-invalid' : ''}`}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Device description"
            autoFocus
          />
          {errors.description && <p className="dp-drawer__error">{errors.description}</p>}
        </div>

        {/* Serial Number */}
        <div className="dp-drawer__field">
          <label className="dp-drawer__label">
            Serial Number <span className="dp-drawer__required">*</span>
          </label>
          <input
            type="text"
            className={`dp-drawer__input${errors.serialNum ? ' is-invalid' : ''}`}
            value={serialNum}
            onChange={(e) => setSerialNum(e.target.value)}
            placeholder="Numeric serial number"
          />
          {errors.serialNum && <p className="dp-drawer__error">{errors.serialNum}</p>}
        </div>

        {/* Behavior (Inovonics) */}
        {!isPalcare && useMapData.length > 0 && (
          <div className="dp-drawer__field">
            <label className="dp-drawer__label">Behavior</label>
            <select
              className="dp-drawer__select"
              value={deviceUseId}
              onChange={(e) => setDeviceUseId(e.target.value)}
            >
              {useMapData.map((item, i) => (
                <option key={i} value={item.inovonics_device_use_id}>
                  {item.option_text || 'Standard'}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Line Profile (Palcare) */}
        {isPalcare && dspProfiles?.length > 0 && (
          <div className="dp-drawer__field">
            <label className="dp-drawer__label">Line Profile</label>
            <select
              className="dp-drawer__select"
              value={dspProfileId}
              onChange={(e) => setDspProfileId(e.target.value)}
            >
              {dspProfiles.map((item, i) => (
                <option key={i} value={item.dsp_profile_id}>
                  {item.profile_name || 'No Profile'}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Assignment (Searchable Dropdown) */}
        <div className="dp-drawer__field">
          <label className="dp-drawer__label">
            {isAreaDevice ? 'Assigned Area' : 'Assigned Resident'}
          </label>
          <SearchableDropdown
            dataset={isAreaDevice
              ? { data: areas, label: 'area_name', value: 'area_id' }
              : { data: residents, label: 'resident_name', value: 'resident_id' }
            }
            label_name={isAreaDevice ? 'Area' : 'Resident'}
            onChange={(val) => setLinkedId(val)}
            value={linkedId}
            clearable={true}
            required={true}
            divClass="w-100"
            id="ip-edit-linked"
          />
        </div>

        {/* Footer */}
        <div className="dp-drawer__footer">
          <button type="button" className="dp-btn dp-btn--cancel" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="submit" className="dp-btn dp-btn--primary" disabled={saving}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default BasicInfoDrawer;
